/** @format */

import React from 'react';
import SuperAgent from 'superagent';
import update from 'immutability-helper';
import {parseEntityError} from 'frontier-form';
import {CartOverviewContext} from 'frontier/cart/components/cart_overview/cart_overview_context';

export default function withCartHandler(ComponentContainer) {
    return class extends React.Component {
        state = {
            loading: true,
            error: false,
            fieldsSaving: [], // Fields that are currently being updated [{orderId: 1, fieldName: proof_type_id}]
            cart: {
                user_id: '',
                status_id: '',
                is_reorder: false,
                subtotal: '', // Labels + Options (does not include shipping, tax, duties, discounts
                order_count: '', // Numer of orderes in the cart
                weight: '' // The calculated weight of the orders
            },
            combinedCartCount: 0, // When orders are autocombined, how many?
            substrates: [], // Grouped Substrates
            orders: [] // Orders in this cart
        };

        /**
         * This is what we send to the server on save
         */
        stateToRequestBody = () => {
            const {orders} = this.state;

            // Only send specific fields
            // Maybe easier to manage with accessible fields?
            const orderUpdateRequest = orders.map(order => {
                if (order.is_reorder) {
                    // Only specific fields can be changed
                    return {
                        id: order.id,
                        proof_type_id: order.proof_type_id,
                        total_qty: order.total_qty,
                        processing_id: order.processing_id,
                        comments_customer: order.comments_customer,
                        comments_preprinting: order.comments_preprinting,
                        designs_orders: order.designs_orders.map(design => {
                            return {id: design.id, qty: design.qty};
                        })
                    };
                }
                return {
                    id: order.id,
                    proof_type_id: order.proof_type_id,
                    form_factor_id: order.form_factor_id,
                    roll_direction_id: order.roll_direction_id,
                    substrate_id: order.substrate_id,
                    laminate_id: order.laminate_id,
                    color_id: order.color_id,
                    total_qty: order.total_qty,
                    processing_id: order.processing_id,
                    comments_customer: order.comments_customer,
                    comments_preprinting: order.comments_preprinting,
                    designs_orders: order.designs_orders.map(design => {
                        return {id: design.id, qty: design.qty};
                    })
                };
            });

            return {orders: orderUpdateRequest};
        };

        /**
         * parse the response.body to our state
         * @param body
         */
        responseBodyToState = ({cart, combinedCount, substrates}) => {
            const {orders, ...cartWithoutOrders} = cart;

            return {
                cart: cartWithoutOrders,
                orders,
                substrates,
                combinedCount
            };
        };

        async componentDidMount() {
            await this.fetch();
        }

        fetch = async () => {
            if (typeof this.abortController === 'undefined') {
                this.abortController = new AbortController();
            } else {
                this.abortController.abort('New Request, Cancel Outdated Request');
                this.abortController = new AbortController();
            }

            if (typeof this.updateTimeout !== 'undefined') {
                clearTimeout(this.updateTimeout);
            }

            this.updateTimeout = setTimeout(async () => {
                try {
                    const response = await fetch('/carts/edit', {
                        signal: this.abortController.signal,
                        method: 'GET',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });

                    const responseBody = await response.json();
                    if (responseBody.code && responseBody.code !== 200) {
                        this.setState({error: {message: responseBody.message}, loading: false});
                    } else {
                        const updatedState = this.responseBodyToState(responseBody);
                        updatedState.loading = false;
                        updatedState.error = false;
                        this.setState(updatedState);
                    }
                } catch (e) {
                    console.log(e);
                    this.setState({loading: false});
                    // Proably aborted
                }
            }, 1000);
        };

        handleChange = updateTarget => {
            this.setState(state => update(state, updateTarget));
        };

        handleDeleteOrder = async orderId => {
            const fieldName = 'deleting';
            this.setState(state =>
                update(state, {
                    fieldsSaving: {$push: [{orderId: orderId, fieldName: fieldName}]}
                })
            );
            const response = await SuperAgent.post('/carts/delete-order')
                .set('accept', 'application/json')
                .send({order_id: orderId});

            await this.fetch();
        };

        handleAllUploadsComplete = (orderId, files) => {
            this.setState(
                state => {
                    const orderIndex = state.orders.findIndex(order => order.id == orderId);
                    const updateTarget = {
                        orders: {[orderIndex]: {designs_orders: {$push: files}}}
                    };
                    return update(state, updateTarget);
                },
                async () => {
                    await this.fetch();
                }
            );
        };

        handleChangeOrder = (orderId, fieldName, updateOrderTarget) => {
            this.setState(
                state => {
                    const orderIndex = state.orders.findIndex(order => order.id == orderId);
                    const updateTarget = {
                        orders: {[orderIndex]: updateOrderTarget},
                        fieldsSaving: {$push: [{orderId: orderId, fieldName: fieldName}]}
                    };
                    return update(state, updateTarget);
                },
                () => {
                    this.save();
                }
            );
        };

        handleDeleteDesign = async (orderId, designId) => {
            const orderIndex = this.state.orders.findIndex(order => order.id == orderId);
            const designIndex = this.state.orders[orderIndex].designs_orders.findIndex(
                design => design.id == designId
            );
            const fieldName = `${designId}.design.file`;

            if (designIndex > -1) {
                this.setState(state => {
                    const updateTarget = {
                        orders: {[orderIndex]: {designs_orders: {$splice: [[designIndex, 1]]}}},
                        fieldsSaving: {
                            $push: [{orderId: orderId, fieldName: fieldName}]
                        }
                    };

                    return update(state, updateTarget);
                });

                //delete the record from our DB
                const deleteResponse = await SuperAgent.delete(
                    '/account/designs/delete/' + designId
                ).set('accept', 'application/json');

                this.setState(
                    state => {
                        const savingIndex = state.fieldsSaving.findIndex(
                            field => field.fieldName === fieldName
                        );
                        const updateTarget = {};
                        if (savingIndex > -1) {
                            updateTarget.fieldsSaving = {$splice: [[savingIndex, 1]]};
                        }
                        return update(state, updateTarget);
                    },
                    async () => {
                        if (this.state.fieldsSaving.length === 0) {
                            await this.fetch();
                        }
                    }
                );
            }
        };

        handleSavedToAccount = async () => {
            await this.fetch();
        };

        /**
         * General utility for saving any new information set for a cart such as chossing a shipping
         * method id and payment information
         */
        save = () => {
            if (typeof this.saveRequest != 'undefined') {
                this.saveRequest.abort();
            }
            this.saveRequest = SuperAgent.post('/carts/edit')
                .set('Accept', 'application/json')
                .send(this.stateToRequestBody());
            this.saveRequest.end((err, response) => {
                if (err || !response.body) {
                    this.setState({
                        error: this.responseToError(err, response.body),
                        fieldsSaving: []
                    });
                } else {
                    const state = this.responseBodyToState(response.body);
                    state.error = false;
                    state.fieldsSaving = [];
                    this.setState(state);
                }
            });
        };

        responseToError = (err, responseBody) => {
            let error = '';
            if (responseBody) {
                error = parseEntityError(responseBody);
            } else {
                error = err;
            }
            return error;
        };

        render() {
            const props = {...this.props};
            props.onChange = this.handleChange.bind(this);

            props.onChangeOrder = this.handleChangeOrder.bind(this);
            props.onAllUploadsComplete = this.handleAllUploadsComplete.bind(this);
            props.onDeleteDesign = this.handleDeleteDesign.bind(this);
            props.onDeleteOrder = this.handleDeleteOrder.bind(this);
            props.onSavedToAccount = this.handleSavedToAccount.bind(this);

            const context = {
                handleChange: this.handleChange.bind(this),
                handleChangeOrder: this.handleChangeOrder.bind(this),
                handleAllUploadsComplete: this.handleAllUploadsComplete.bind(this),
                handleDeleteDesign: this.handleDeleteDesign.bind(this),
                handleDeleteOrder: this.handleDeleteOrder.bind(this),
                handleSavedToAccount: this.handleSavedToAccount.bind(this),
                handleFetchCart: this.fetch.bind(this),
                ...this.state
            };

            // TODO, Use Context exclusively

            return (
                <CartOverviewContext.Provider value={context}>
                    <ComponentContainer {...props} {...this.state} />
                </CartOverviewContext.Provider>
            );
        }
    };
}
